import { useEffect, useRef, useState } from 'react';
import { orderService, Order } from '../services';

/**
 * Alerta sonoro da cozinha (3 notas E5-G5-C6) usando Web Audio —
 * sem arquivos de áudio. Reaproveitado na tela de Pedidos e na TV da cozinha.
 */
export function tocarAlertaCozinha() {
  try {
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AudioCtx();
    const notas = [659.25, 783.99, 1046.5]; // E5, G5, C6
    notas.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const inicio = ctx.currentTime + i * 0.18;
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.001, inicio);
      gain.gain.exponentialRampToValueAtTime(0.25, inicio + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, inicio + 0.16);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(inicio);
      osc.stop(inicio + 0.18);
    });
  } catch {
    // áudio indisponível — segue sem som
  }
}

export interface UsePedidosCozinhaResultado {
  orders: Order[];
  isLoading: boolean;
  filterStatus: string;
  setFilterStatus: (status: string) => void;
  atualizarStatus: (orderId: string, newStatus: string) => Promise<void>;
  novoTitulo?: string;
  /** T117: true quando o polling falhou 2x seguidas (aviso "sem conexão") */
  erroConexao: boolean;
  /** T117: erro da última ação (substitui o alert() nativo) */
  erroAcao: string;
  limparErroAcao: () => void;
  /** Recarrega a lista na hora (sem esperar o polling) */
  recarregar: () => void;
  /** Publica um erro de ação na faixa de aviso da tela */
  registrarErroAcao: (msg: string) => void;
}

/**
 * Hook compartilhado das telas da cozinha: carrega os pedidos, atualiza
 * sozinho (polling), toca alerta para pedidos novos ou reabertos e muda o
 * título da aba quando chega algo.
 */
export function usePedidosCozinha(tituloAba = 'Pedidos - PrimeClick'): UsePedidosCozinhaResultado {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('ATIVOS');
  const statusesVistos = useRef<Map<string, string>>(new Map());
  const [novoTitulo, setNovoTitulo] = useState<string | undefined>(undefined);
  // T117: falha de rede precisa de aviso na tela (antes só ia para o console)
  const falhasSeguidas = useRef(0);
  const [erroConexao, setErroConexao] = useState(false);
  const [erroAcao, setErroAcao] = useState('');

  useEffect(() => {
    statusesVistos.current.clear();
    loadOrders();
    // Atualiza sozinho: pedidos novos (ou reabertos) disparam som + alerta
    const timer = setInterval(() => loadOrders(true), 5000);
    return () => clearInterval(timer);
  }, [filterStatus]);

  const loadOrders = async (silencioso = false) => {
    if (!silencioso) setIsLoading(true);
    try {
      const response = await orderService.findAll({
        page: 1,
        // T116: limite maior e mais NOVOS primeiro — em movimento alto o pedido
        // recém-criado não pode ficar fora da página (a cozinha perdia o pedido).
        limit: 200,
        status: filterStatus === 'ATIVOS' ? undefined : filterStatus || undefined,
        ordenar: 'desc',
      });

      // Exibição continua em ordem de fila (mais antigo primeiro)
      const pedidos = [...response.data.data].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );

      // T117: conexão voltou
      falhasSeguidas.current = 0;
      setErroConexao(false);
      const novos = pedidos.filter((o) => !statusesVistos.current.has(o.id));
      // Pedido já visto que voltou a precisar da cozinha (ex: comanda "Pronta"
      // recebeu mais itens e voltou para "Em preparo") ou que pediu a conta
      const reabertos = pedidos.filter((o) => {
        const anterior = statusesVistos.current.get(o.id);
        return (
          anterior !== undefined &&
          anterior !== o.status &&
          ['PENDING', 'CONFIRMED', 'PREPARING', 'REQUESTED_BILL'].includes(o.status)
        );
      });

      if (silencioso && (novos.length > 0 || reabertos.length > 0)) {
        const total = novos.length + reabertos.length;
        tocarAlertaCozinha();
        const titulo = `🔔 ${total} pedido${total > 1 ? 's' : ''} - PrimeClick`;
        document.title = titulo;
        setNovoTitulo(titulo);
        setTimeout(() => {
          document.title = tituloAba;
          setNovoTitulo(undefined);
        }, 6000);
      }

      for (const o of pedidos) {
        statusesVistos.current.set(o.id, o.status);
      }

      setOrders(pedidos);
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
      // T117: 2 falhas seguidas = aviso visível na tela (não só no console)
      falhasSeguidas.current += 1;
      if (falhasSeguidas.current >= 2) setErroConexao(true);
    } finally {
      if (!silencioso) setIsLoading(false);
    }
  };

  const atualizarStatus = async (orderId: string, newStatus: string) => {
    setErroAcao('');
    try {
      await orderService.updateStatus(orderId, { status: newStatus });
      await loadOrders(true);
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      // T117: mensagem no padrão da tela, no lugar do alert() nativo
      setErroAcao('Não foi possível atualizar o pedido. Tente novamente.');
    }
  };

  return {
    orders,
    isLoading,
    filterStatus,
    setFilterStatus,
    atualizarStatus,
    novoTitulo,
    erroConexao,
    erroAcao,
    limparErroAcao: () => setErroAcao(''),
    recarregar: () => {
      void loadOrders(true);
    },
    registrarErroAcao: (msg: string) => setErroAcao(msg),
  };
}